import { useState } from "react";
import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import apis from "../../../context/api";

// Mirrors the server password policy. Each rule tests the new password live.
const POLICY = [
  { label: "At least 8 characters", test: (v) => v.length >= 8 },
  { label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { label: "One lowercase letter", test: (v) => /[a-z]/.test(v) },
  { label: "One number", test: (v) => /[0-9]/.test(v) },
  { label: "One special character", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const passwordMeetsPolicy = (v) => POLICY.every((rule) => rule.test(v));

function ChangePasswordDialog({ open, onClose, onSuccess }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setServerError("");
    setTriedSubmit(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const passwordsMatch = newPassword === confirmPassword;
  const policyOk = passwordMeetsPolicy(newPassword);
  const clientValid =
    currentPassword.length > 0 && policyOk && passwordsMatch && confirmPassword.length > 0;

  const handleSubmit = async () => {
    setServerError("");
    setTriedSubmit(true);
    if (!clientValid) return;

    setSubmitting(true);
    try {
      await apis.changePassword({ currentPassword, newPassword, confirmPassword });
      reset();
      onSuccess(); // parent shows the success toast and closes the dialog
    } catch (err) {
      // fetchWithAuth already parses { error } into err.message; 401 redirects to login.
      setServerError(err.message || "Failed to change password.");
      setSubmitting(false);
    }
  };

  // endAdornment show/hide toggle for a password field
  const toggle = (shown, setShown) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={() => setShown(!shown)} edge="end" size="small">
          <Icon fontSize="small">{shown ? "visibility_off" : "visibility"}</Icon>
        </IconButton>
      </InputAdornment>
    ),
  });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        <MDBox component="form" role="form" pt={1}>
          <MDBox mb={2}>
            <MDInput
              type={showCurrent ? "text" : "password"}
              label="Current Password"
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              InputProps={toggle(showCurrent, setShowCurrent)}
              error={triedSubmit && currentPassword.length === 0}
            />
          </MDBox>

          <MDBox mb={1}>
            <MDInput
              type={showNew ? "text" : "password"}
              label="New Password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={toggle(showNew, setShowNew)}
              error={triedSubmit && !policyOk}
            />
          </MDBox>

          {/* Live policy hints */}
          <MDBox mb={2} ml={0.5}>
            {POLICY.map((rule) => {
              const ok = rule.test(newPassword);
              return (
                <MDBox key={rule.label} display="flex" alignItems="center" lineHeight={1.3}>
                  <Icon fontSize="small" color={ok ? "success" : "error"}>
                    {ok ? "check_circle" : "cancel"}
                  </Icon>
                  <MDTypography variant="caption" color={ok ? "success" : "text"} sx={{ ml: 0.5 }}>
                    {rule.label}
                  </MDTypography>
                </MDBox>
              );
            })}
          </MDBox>

          <MDBox mb={1}>
            <MDInput
              type={showConfirm ? "text" : "password"}
              label="Confirm New Password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={toggle(showConfirm, setShowConfirm)}
              error={confirmPassword.length > 0 && !passwordsMatch}
            />
          </MDBox>
          {confirmPassword.length > 0 && !passwordsMatch && (
            <MDTypography variant="caption" color="error">
              Passwords do not match.
            </MDTypography>
          )}

          {serverError && (
            <MDBox mt={2}>
              <MDTypography variant="button" color="error" fontWeight="medium">
                {serverError}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={handleClose} color="secondary" variant="outlined">
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSubmit}
          color="info"
          variant="gradient"
          disabled={submitting || (triedSubmit && !clientValid)}
        >
          {submitting ? "Saving..." : "Change Password"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

ChangePasswordDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ChangePasswordDialog;
